// @flow
/** @jsx jsx */

import _ from 'lodash';
import React, { type ComponentType } from 'react';

import { jsx } from '@emotion/core';
import { labelStyles } from '@zapier/common-utils';
import { useStaticAssetPath } from '@zapier/common-components';
import { Colors, Zindexes } from '@zapier/theme';

import { cloneComponentChildren } from 'app/common/utils/ReactUtils';

const GLOBAL_APP_COUNT = '5,000';

type getTitleProps = {
  data: {},
  teamLogoUrl: string,
  accountName: string,
  appTitle: string,
  isOneService: boolean,
  isInvitation: boolean,
  isLogin: boolean,
  isTeamMemberInvitation: boolean,
  isTwoService: boolean,
  isZt: boolean,
  query: {
    reauth?: boolean,
    use_case?: string,
  },
};

export type SplitLoginPageProps = {
  ...$Exact<getTitleProps>,
  Promo: ComponentType<any>,
  children: ComponentType<getTitleProps>,
  topLink: string,
};

const twoColumnMediaQuery = `@media screen and (min-width: 750px)`;
const boxedMediaQuery = `@media screen and (min-width: 1000px)`;

const Styles = labelStyles(__filename, {
  root: () => [
    {
      alignItems: 'center',
      background: Colors.white,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      width: '100%',
      zIndex: Zindexes.signUp,

      [boxedMediaQuery]: {
        background: Colors.storm,
      },
    },
  ],

  modal: props => [
    {
      position: 'relative',
      backgroundColor: Colors.white,
      borderRadius: 4,
      margin: '40px auto',
      maxWidth: props.query.reauth ? 500 : 960,
      padding: 30,
      width: '100%',

      [twoColumnMediaQuery]: {
        minHeight: props.query.reauth ? 'auto' : 500,
        marginTop: undefined,
      },

      [boxedMediaQuery]: {
        border: `1px solid ${Colors.adamantium}`,
        padding: '30px 60px 60px',
      },
    },
  ],

  divider: {
    backgroundColor: Colors.adamantium,
    display: 'none',
    height: '100%',
    width: 1,

    [twoColumnMediaQuery]: {
      display: 'block',
    },
  },

  footer: {
    marginTop: '35px',
  },

  modalTitle: {
    color: Colors.deepspace,
    fontSize: '1.6rem',
    margin: '0 0 30px',
    padding: '0',
    textAlign: 'center',

    [twoColumnMediaQuery]: {
      fontSize: '2rem',
    },
  },

  modalTitleTeamIcon: {
    borderRadius: 3,
    height: 30,
    marginLeft: 8,
    verticalAlign: -7,
    width: 30,
  },

  modalContent: props => [
    {
      [twoColumnMediaQuery]: {
        alignItems: 'center',
        display: 'grid',
        gridGap: 30,
        gridTemplateColumns: props.query.reauth
          ? ''
          : 'minmax(0, 1fr) 1px minmax(0, 1fr)',
      },

      [boxedMediaQuery]: {
        gridGap: 50,
      },
    },
  ],
});

const SecondaryContent = (props: SplitLoginPageProps) => (
  <div>
    <props.Promo {...props} isCondensed={false} />
  </div>
);

const getTitle = ({
  isZt,
  isOneService,
  isTwoService,
  isLogin,
  isInvitation,
  isTeamMemberInvitation,
  accountName,
  appTitle,
  data,
  query,
}: getTitleProps) => {
  const isReauth = _.get(query, 'reauth', false);

  if (isReauth) {
    return "Let's Make Sure It's You";
  }

  if (isInvitation) {
    return `Join ${accountName} on Zapier`;
  }

  if (isLogin) {
    return 'Log In to Your Account';
  }

  if (isZt) {
    const headZtServiceName = _.get(data, [
      'zapTemplateById',
      'headService',
      'name',
    ]);
    const tailZtServiceName = _.get(data, [
      'zapTemplateById',
      'tailService',
      'name',
    ]);

    if (headZtServiceName === tailZtServiceName) {
      return 'Connect the Apps You Use Every Day';
    }

    return `Connect ${headZtServiceName} + ${tailZtServiceName}`;
  }

  if (isTeamMemberInvitation) {
    return `Join the ${appTitle} Team on Zapier`;
  }

  const headServiceName = _.get(data, ['headService', 'name']);
  if (isOneService) {
    return `Connect ${headServiceName} With ${GLOBAL_APP_COUNT}+ Apps`;
  }

  const tailServiceName = _.get(data, ['tailService', 'name']);
  if (isTwoService) {
    return `Connect ${headServiceName} + ${tailServiceName}`;
  }

  return null;
};

const SplitLoginPage = (props: SplitLoginPageProps) => {
  const {
    Promo,
    topLink,
    isZt,
    isOneService,
    isTwoService,
    isLogin,
    isInvitation,
    accountName,
    teamLogoUrl,
    data,
  } = props;

  const { buildStaticAssetUrl } = useStaticAssetPath();

  const hasSecondaryContent = !!Promo;

  const secondaryContent = hasSecondaryContent && (
    <React.Fragment>
      <SecondaryContent {...props} />
      <div css={Styles.divider} />
    </React.Fragment>
  );

  const title = getTitle(props);
  const titleNode = title && (
    <h1 css={Styles.modalTitle}>
      {title}
      {isInvitation && (
        <img
          alt="team logo"
          css={Styles.modalTitleTeamIcon}
          src={teamLogoUrl}
        />
      )}
    </h1>
  );

  return (
    <div css={Styles.root(props, buildStaticAssetUrl)}>
      <div css={Styles.modal(props)}>
        {titleNode}
        <div css={Styles.modalContent(props)}>
          {secondaryContent}
          <div>
            <div>
              {cloneComponentChildren(props.children, {
                isZt,
                isLogin,
                isInvitation,
                isOneService,
                isTwoService,
                topLink,
                teamLogoUrl,
                accountName,
                data,
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SplitLoginPage;
